/**
 * G3 runtime — register components; init on load and theme editor section load.
 */
(function () {
  'use strict';

  const registry = new Map();

  function initContainer(container) {
    if (!container || typeof container.querySelectorAll !== 'function') return;
    container.querySelectorAll('[data-g3-component]').forEach((el) => {
      const name = el.getAttribute('data-g3-component');
      if (!name) return;
      const fn = registry.get(name);
      if (typeof fn === 'function' && !el.dataset.g3Initialized) {
        el.dataset.g3Initialized = 'true';
        try {
          fn(el);
        } catch (e) {
          console.warn('[G3]', name, e);
        }
      }
    });
  }

  function register(name, initFn) {
    if (!name || typeof initFn !== 'function') return;
    registry.set(name, initFn);
  }

  window.G3Theme = {
    register,
    init(root) {
      initContainer(root || document);
    },
  };

  /** Generic horizontal track + optional prev/next */
  register('slider', (root) => {
    const track = root.querySelector('.g3-slider__track');
    const prev = root.querySelector('[data-g3-slider-prev]');
    const next = root.querySelector('[data-g3-slider-next]');
    if (!track) return;

    const slideWidth = () => track.querySelector('.g3-slider__slide')?.offsetWidth || track.clientWidth;

    function scrollStep(dir) {
      track.scrollBy({ left: dir * slideWidth(), behavior: 'smooth' });
    }

    prev?.addEventListener('click', () => scrollStep(-1));
    next?.addEventListener('click', () => scrollStep(1));
  });

  /** G3 home banner: arrows + dots + scroll sync + autoplay */
  register('home-banner-slider', (root) => {
    const track = root.querySelector('.g3-home-banner__track');
    const dots  = Array.prototype.slice.call(root.querySelectorAll('[data-g3-pager-index]'));
    const btnPrev = root.querySelector('[data-g3-prev]');
    const btnNext = root.querySelector('[data-g3-next]');
    if (!track) return;

    let autoplayTimer = null;
    let syncDebounce  = null;

    const slideWidth = () => {
      const el = track.querySelector('.g3-slider__slide');
      return el ? el.offsetWidth : track.clientWidth;
    };

    const currentIndex = () => {
      const w = slideWidth();
      return w ? Math.min(Math.round(track.scrollLeft / w), Math.max(dots.length - 1, 0)) : 0;
    };

    function goToIndex(i) {
      if (!track.isConnected) return;
      const w   = slideWidth();
      const max = Math.max(dots.length - 1, 0);
      track.scrollTo({ left: Math.max(0, Math.min(i, max)) * w, behavior: 'smooth' });
    }

    function syncDots() {
      if (!track.isConnected) return;
      const idx = currentIndex();
      dots.forEach((dot, j) => {
        const on = j === idx;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    /* Use scrollend when available (Chrome 114+/Firefox 109+), debounce as fallback */
    const supportsScrollEnd = 'onscrollend' in window;
    if (supportsScrollEnd) {
      track.addEventListener('scrollend', syncDots);
    } else {
      track.addEventListener('scroll', () => {
        clearTimeout(syncDebounce);
        syncDebounce = setTimeout(syncDots, 80);
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.getAttribute('data-g3-pager-index'), 10);
        if (!Number.isNaN(i)) goToIndex(i);
      });
    });

    btnPrev && btnPrev.addEventListener('click', () => goToIndex(currentIndex() - 1));
    btnNext && btnNext.addEventListener('click', () => goToIndex(currentIndex() + 1));

    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }

    function startAutoplay() {
      stopAutoplay();
      if (root.dataset.g3Autoplay !== 'true' || dots.length < 2) return;
      const ms = parseInt(root.dataset.g3AutoplayMs, 10) || 5000;
      autoplayTimer = window.setInterval(() => {
        if (!track.isConnected) { stopAutoplay(); return; }
        goToIndex((currentIndex() + 1) % dots.length);
      }, ms);
    }

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('touchstart', stopAutoplay, { passive: true });
    root.addEventListener('touchend', () => window.setTimeout(startAutoplay, 2000));
    window.addEventListener('resize', syncDots);

    syncDots();
    startAutoplay();
  });

  /** G3 Testimonials: slider (> 6 cards) + play button */
  register('testimonials', (root) => {
    /* ── Slider ── */
    if (root.dataset.g3IsSlider === 'true') {
      var track   = root.querySelector('[data-g3-tm-track]');
      var dots    = Array.prototype.slice.call(root.querySelectorAll('[data-g3-tm-dot]'));
      var btnPrev = root.querySelector('[data-g3-tm-prev]');
      var btnNext = root.querySelector('[data-g3-tm-next]');
      var cols    = parseInt(root.dataset.g3TmCols, 10) || 4;
      var syncDb  = null;

      if (track) {
        var pageCount = dots.length;

        var currentPage = function () {
          return track.clientWidth ? Math.min(Math.round(track.scrollLeft / track.clientWidth), pageCount - 1) : 0;
        };

        var goToPage = function (p) {
          p = Math.max(0, Math.min(p, pageCount - 1));
          track.scrollTo({ left: p * track.clientWidth, behavior: 'smooth' });
        };

        var syncDots = function () {
          var p = currentPage();
          dots.forEach(function (d, i) {
            var on = i === p;
            d.classList.toggle('is-active', on);
            d.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          if (btnPrev) btnPrev.disabled = p === 0;
          if (btnNext) btnNext.disabled = p === pageCount - 1;
        };

        var supportsScrollEnd = 'onscrollend' in window;
        if (supportsScrollEnd) {
          track.addEventListener('scrollend', syncDots);
        } else {
          track.addEventListener('scroll', function () {
            clearTimeout(syncDb);
            syncDb = setTimeout(syncDots, 80);
          });
        }

        dots.forEach(function (d) {
          d.addEventListener('click', function () {
            var i = parseInt(d.getAttribute('data-g3-tm-dot'), 10);
            if (!isNaN(i)) goToPage(i);
          });
        });

        if (btnPrev) btnPrev.addEventListener('click', function () { goToPage(currentPage() - 1); });
        if (btnNext) btnNext.addEventListener('click', function () { goToPage(currentPage() + 1); });

        window.addEventListener('resize', syncDots);
        syncDots();
      }
    }

    /* ── Video play ── */
    root.querySelectorAll('.g3-testimonials__card[data-g3-video-url]').forEach((card) => {
      const playBtn  = card.querySelector('.g3-testimonials__play-btn');
      if (!playBtn) return;

      const videoUrl  = card.dataset.g3VideoUrl || '';
      const videoType = card.dataset.g3VideoType || 'shopify';

      function getYouTubeId(url) {
        var m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
        return m ? m[1] : null;
      }

      function getVimeoId(url) {
        var m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        return m ? m[1] : null;
      }

      function activateVideo() {
        if (card.classList.contains('is-playing')) return;
        card.classList.add('is-playing');

        var wrap = document.createElement('div');
        wrap.className = 'g3-testimonials__iframe-wrap';

        if (videoType === 'youtube') {
          var id = getYouTubeId(videoUrl);
          if (!id) return;
          var iframe = document.createElement('iframe');
          iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&playsinline=1&rel=0';
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = 'YouTube video';
          wrap.appendChild(iframe);
        } else if (videoType === 'vimeo') {
          var vid = getVimeoId(videoUrl);
          if (!vid) return;
          var iframe = document.createElement('iframe');
          iframe.src = 'https://player.vimeo.com/video/' + vid + '?autoplay=1&playsinline=1';
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = 'Vimeo video';
          wrap.appendChild(iframe);
        } else {
          /* Shopify-hosted: create <video> element */
          var video = document.createElement('video');
          video.src = videoUrl;
          video.autoplay = true;
          video.playsinline = true;
          video.controls = true;
          video.className = 'g3-testimonials__video-el';
          video.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;';
          card.querySelector('.g3-testimonials__media').appendChild(video);
          video.play().catch(function () {});
          return;
        }

        card.querySelector('.g3-testimonials__media').appendChild(wrap);
      }

      playBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        activateVideo();
      });
    });
  });

  /** G3 Review Slider: multi-card scroll + dots + arrows + autoplay */
  register('review-slider', (root) => {
    var track   = root.querySelector('[data-g3-rs-track]');
    var dots    = Array.prototype.slice.call(root.querySelectorAll('[data-g3-rs-dot]'));
    var btnPrev = root.querySelector('[data-g3-rs-prev]');
    var btnNext = root.querySelector('[data-g3-rs-next]');
    if (!track) return;

    var cols      = parseInt(root.dataset.g3RsCols, 10) || 3;
    var total     = dots.length;
    var syncDb    = null;
    var autoTimer = null;
    var isSingle  = cols === 1;

    /* gap: getComputedStyle().gap can return "normal" in Safari → use columnGap longhand */
    var trackGap = function () {
      var cs  = getComputedStyle(track);
      var raw = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
      return isNaN(raw) ? 0 : raw;
    };

    var cardEl = function () { return track.querySelector('.g3-review-slider__card'); };

    var cardWidth = function () {
      var card = cardEl();
      return card ? card.offsetWidth : track.clientWidth;
    };

    /* For center-snap (single-col) the scroll target accounts for track padding + half-card centering.
       For start-snap (multi-col) it's simply i × (cardWidth + gap). */
    var scrollLeft = function (i) {
      var cw  = cardWidth();
      var gap = trackGap();
      if (isSingle) {
        /* track padding = computed paddingLeft */
        var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        /* center of card i relative to track content start */
        var cardCenter = pad + i * (cw + gap) + cw / 2;
        return Math.max(0, cardCenter - track.clientWidth / 2);
      }
      return i * (cw + gap);
    };

    var currentIndex = function () {
      if (!total) return 0;
      var cw  = cardWidth();
      var gap = trackGap();
      if (isSingle) {
        var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        var step = cw + gap;
        /* invert the scrollLeft formula: idx = (scrollLeft + halfViewport - pad - halfCard) / step */
        var idx = Math.round((track.scrollLeft + track.clientWidth / 2 - pad - cw / 2) / step);
        return Math.max(0, Math.min(idx, total - 1));
      }
      var w = cw + gap;
      return w ? Math.min(Math.round(track.scrollLeft / w), total - 1) : 0;
    };

    var goTo = function (i) {
      i = Math.max(0, Math.min(i, total - 1));
      track.scrollTo({ left: scrollLeft(i), behavior: 'smooth' });
    };

    var syncDots = function () {
      var idx = currentIndex();
      dots.forEach(function (d, i) {
        var on = i === idx;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (btnPrev) btnPrev.disabled = idx === 0;
      if (btnNext) btnNext.disabled = total > cols ? idx >= total - cols : false;
    };

    var stopAuto  = function () { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } };
    var startAuto = function () {
      stopAuto();
      if (root.dataset.g3Autoplay !== 'true') return;
      var ms = parseInt(root.dataset.g3AutoplayMs, 10) || 5000;
      autoTimer = setInterval(function () {
        if (!track.isConnected) { stopAuto(); return; }
        var maxIdx = total > cols ? total - cols : 0;
        var next   = currentIndex() + 1;
        goTo(next > maxIdx ? 0 : next);
      }, ms);
    };

    var supportsScrollEnd = 'onscrollend' in window;
    if (supportsScrollEnd) {
      track.addEventListener('scrollend', syncDots);
    } else {
      track.addEventListener('scroll', function () {
        clearTimeout(syncDb);
        syncDb = setTimeout(syncDots, 80);
      });
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        var i = parseInt(d.getAttribute('data-g3-rs-dot'), 10);
        if (!isNaN(i)) { stopAuto(); goTo(i); }
      });
    });

    if (btnPrev) btnPrev.addEventListener('click', function () { stopAuto(); goTo(currentIndex() - 1); });
    if (btnNext) btnNext.addEventListener('click', function () { stopAuto(); goTo(currentIndex() + 1); });

    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('touchstart', stopAuto, { passive: true });
    root.addEventListener('touchend', function () { setTimeout(startAuto, 2000); });
    window.addEventListener('resize', syncDots);

    syncDots();
    startAuto();
  });

  /** G3 Pro Slider: dots + arrows + optional autoplay */
  register('pro-slider', (root) => {
    var track   = root.querySelector('[data-g3-ps-track]');
    var dots    = Array.prototype.slice.call(root.querySelectorAll('[data-g3-ps-dot]'));
    var btnPrev = root.querySelector('[data-g3-ps-prev]');
    var btnNext = root.querySelector('[data-g3-ps-next]');
    if (!track || dots.length < 2) return;

    var total     = dots.length;
    var syncDb    = null;
    var autoTimer = null;

    var currentIndex = function () {
      return track.clientWidth ? Math.min(Math.round(track.scrollLeft / track.clientWidth), total - 1) : 0;
    };

    var goTo = function (i) {
      i = Math.max(0, Math.min(i, total - 1));
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    };

    var syncDots = function () {
      var idx = currentIndex();
      dots.forEach(function (d, i) {
        var on = i === idx;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (btnPrev) btnPrev.disabled = idx === 0;
      if (btnNext) btnNext.disabled = idx === total - 1;
    };

    var stopAuto  = function () { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } };
    var startAuto = function () {
      stopAuto();
      if (root.dataset.g3Autoplay !== 'true') return;
      var ms = parseInt(root.dataset.g3AutoplayMs, 10) || 6000;
      autoTimer = setInterval(function () {
        if (!track.isConnected) { stopAuto(); return; }
        goTo((currentIndex() + 1) % total);
      }, ms);
    };

    var supportsScrollEnd = 'onscrollend' in window;
    if (supportsScrollEnd) {
      track.addEventListener('scrollend', syncDots);
    } else {
      track.addEventListener('scroll', function () {
        clearTimeout(syncDb);
        syncDb = setTimeout(syncDots, 80);
      });
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        var i = parseInt(d.getAttribute('data-g3-ps-dot'), 10);
        if (!isNaN(i)) { stopAuto(); goTo(i); }
      });
    });

    if (btnPrev) btnPrev.addEventListener('click', function () { stopAuto(); goTo(currentIndex() - 1); });
    if (btnNext) btnNext.addEventListener('click', function () { stopAuto(); goTo(currentIndex() + 1); });

    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('touchstart', stopAuto, { passive: true });
    root.addEventListener('touchend', function () { setTimeout(startAuto, 2000); });
    window.addEventListener('resize', syncDots);

    syncDots();
    startAuto();
  });

  document.addEventListener('DOMContentLoaded', () => window.G3Theme.init(document));
  document.addEventListener('shopify:section:load', (event) => window.G3Theme.init(event.target));
})();
